import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { SleepProfileFormValues } from "./interface";
import useGetSleepProfile from "hooks/api/client/useGetSleepProfile";
import useUpsertSleepProfile from "hooks/api/client/useUpsertSleepProfile";

const EMPTY_VALUES: SleepProfileFormValues = {
  notableSleepConcerns: "",
  bedtimeRoutine: "",
  sleepsThroughNight: "",
  takesNapsRegularly: "",
  wakesAndGetsOut: "",
  wakesAndSeeksAdult: "",
};

// DB stores booleans; form uses "YES" / "NO" strings
const boolToStr = (val: boolean | null | undefined): string => {
  if (val === true) return "YES";
  if (val === false) return "NO";
  return "";
};

const strToBool = (val: string): boolean | null => {
  if (val === "YES") return true;
  if (val === "NO") return false;
  return null;
};

export const SleepProfile: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { sleepProfile, refetchSleepProfile } = useGetSleepProfile(clientIdNum);

  const { upsertSleepProfileMutation, isUpsertSleepProfileLoading } = useUpsertSleepProfile({
    onSuccess: () => {
      refetchSleepProfile();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<SleepProfileFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<SleepProfileFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (sleepProfile) {
      const mapped: SleepProfileFormValues = {
        notableSleepConcerns: sleepProfile.notableSleepConcerns || "",
        bedtimeRoutine: sleepProfile.bedtimeRoutine || "",
        sleepsThroughNight: boolToStr(sleepProfile.sleepsThroughNight),
        takesNapsRegularly: boolToStr(sleepProfile.takesNapsRegularly),
        wakesAndGetsOut: boolToStr(sleepProfile.wakesAndGetsOut),
        wakesAndSeeksAdult: boolToStr(sleepProfile.wakesAndSeeksAdult),
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [sleepProfile]);

  const handleChange =
    (field: keyof SleepProfileFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    const payload = {
      notableSleepConcerns: formValues.notableSleepConcerns || null,
      bedtimeRoutine: formValues.bedtimeRoutine || null,
      sleepsThroughNight: strToBool(formValues.sleepsThroughNight),
      takesNapsRegularly: strToBool(formValues.takesNapsRegularly),
      wakesAndGetsOut: strToBool(formValues.wakesAndGetsOut),
      wakesAndSeeksAdult: strToBool(formValues.wakesAndSeeksAdult),
    };
    upsertSleepProfileMutation({ clientId: clientIdNum, payload });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertSleepProfileLoading;

  return (
    <SectionWrapper
      title="Sleep Profile"
      description="Sleep habits, routines, and any concerns that may shape how the client responds during sessions."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default SleepProfile;
